import { createContext, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import {
    getItem as getItemFromIndexDB,
    ObjectStore,
    setItem as setItemInIndexDB,
} from '../service/cache';
import {
    firstRender,
    locationDefaultKeyReplacement,
    sessionID,
    setFirstRender,
} from '../util/util';
const mostRecentPageStorageKey = 'streamsix_most_recent_page';

type PageDataContextType = {
    isMostRecentStored: boolean;
    isLoadFromMostRecent: boolean;
    isLoadFromCache: boolean;
    setItem: Function;
    getItem: any;
    isLoading: boolean;
    mostRecentPageData: PageData;
};

export type PageData = {
    pathname: string;
    data: { [key: string]: string };
};

export type SessionData = {
    [key: string]: PageData;
};

export const PageDataContext = createContext<PageDataContextType>({
    isMostRecentStored: false,
    isLoadFromMostRecent: false,
    isLoadFromCache: false,
    setItem: () => {},
    getItem: () => {},
    isLoading: true,
    mostRecentPageData: { pathname: '', data: {} },
});

export default PageDataContext;

export const PageDataContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const initialPageValue: PageData = { data: {}, pathname: '' };
    const initialSessionValue: SessionData = { '': initialPageValue };

    const [loading, setIsLoading] = useState(true);
    const [isNewPage, setIsNewPage] = useState(true);
    const location = useLocation();

    const getSessionData = async (): Promise<SessionData> => {
        const storedSessionData = await getItemFromIndexDB(
            ObjectStore.pageDataStoreName,
            sessionID,
            initialSessionValue,
        );

        return storedSessionData;
    };

    const getMostRecentPageData = async (): Promise<PageData> => {
        return await getItemFromIndexDB(
            ObjectStore.mostRecentPageStoreName,
            mostRecentPageStorageKey,
            initialPageValue,
        );
    };

    const [sessionData, setSessionData] = useState<SessionData>(() => {
        const getStoredData = async () => {
            const sessionData = await getSessionData();
            const mostRecentPageData = await getMostRecentPageData();
            setSessionData({
                ...sessionData,
                [mostRecentPageStorageKey]: mostRecentPageData,
                [getPageKey()]: mostRecentPageData,
            });
            setIsLoading(false);
        };
        getStoredData();
        return {};
    });

    const getPageKey = (): string => {
        if (location.key === 'default') {
            return locationDefaultKeyReplacement;
        }
        return location.key;
    };

    const setItem = <T,>(key: string, value: T) => {
        const pageKey = getPageKey();

        setSessionData((prevPageData) => {
            let newPageData = {
                data: {
                    ...prevPageData[pageKey]?.data,
                    [key]: JSON.stringify(value),
                },
                pathname: location.pathname,
            };

            if (isLoadFromMostRecent()) {
                newPageData = {
                    data: {
                        ...prevPageData?.[mostRecentPageStorageKey].data,
                        ...newPageData.data,
                    },
                    pathname: location.pathname,
                };
            }

            const pageData = {
                ...prevPageData,
                [getPageKey()]: newPageData,
            };

            saveSessionData(pageData);
            saveMostRecentPageData(newPageData);

            return pageData;
        });
    };

    const getItem = <T,>(initialValue: T, key: string): T => {
        const pageKey = getPageKey();
        const data = isLoadFromMostRecent()
            ? sessionData[mostRecentPageStorageKey]?.data[key]
            : sessionData[pageKey]?.data[key];
        if (!!data) {
            try {
                const value = JSON.parse(data) as T;
                return value;
            } catch (e) {
                console.error(`[MostRecentPageContext] error parsing value ${data} error: ${e}`);
            }
        }

        return initialValue;
    };

    const isMostRecentPageStored = (): boolean => {
        return !!sessionData?.[mostRecentPageStorageKey]?.pathname;
    };

    const isLoadFromMostRecent = (): boolean => {
        const value = location?.state?.loadMostRecentPageData;
        return !!value;
    };

    const isLoadFromCache = (): boolean => {
        return isNewPage || isLoadFromMostRecent();
    };

    const saveSessionData = (sessionData: SessionData) => {
        setItemInIndexDB(`${sessionID}`, sessionData, ObjectStore.pageDataStoreName);
    };

    const saveMostRecentPageData = (pageData: PageData) => {
        setItemInIndexDB(mostRecentPageStorageKey, pageData, ObjectStore.mostRecentPageStoreName);
    };

    const initPageData = () => {
        setSessionData((prevPageData) => {
            const pageKey = getPageKey();

            //if current page doesn't exist in session data, add a blank page.
            const sessionData: SessionData = {
                [pageKey]: { data: {}, pathname: location.pathname },
                ...prevPageData,
            };

            setSessionData(sessionData);
            saveSessionData(sessionData);
            saveMostRecentPageData(sessionData?.[pageKey]);
            return sessionData;
        });
    };

    useMemo(() => {
        //this is a hacky way to get around the react strict mode
        if (process.env.NODE_ENV !== 'production') {
            if (firstRender) {
                setFirstRender(false);
                return;
            }
        }
        //end of hack

        setIsNewPage(!!sessionData?.[getPageKey()]);
    }, [location.key]);

    useEffect(() => {
        if (!loading) {
            initPageData();
        }
    }, [location.key, loading]);

    return (
        <PageDataContext.Provider
            value={{
                setItem: setItem,
                getItem: getItem,
                isLoadFromMostRecent: isLoadFromMostRecent(),
                isMostRecentStored: isMostRecentPageStored(),
                isLoadFromCache: isLoadFromCache(),
                isLoading: loading,
                mostRecentPageData: sessionData[mostRecentPageStorageKey],
            }}
        >
            {children}
        </PageDataContext.Provider>
    );
};
